import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Modal, Skeleton } from '@/components/ui/daisy';
import { useModalDialog } from '@/components/ui/daisy/modal/hooks';
import { CacheManager } from '@/data/hook/query/v2/CacheFacade';
import { publishLessons } from '@/data/services/coursePathServices';
import {
  lessonBlocksQueryKeys,
  lessonsQueryKeys,
} from '@/data/services/querykeys';
import { usePublishableLessons } from '@/hooks/lessons/usePublishableLessons';
import CoursePath from '@/models/CoursePath';
import { getErrorMessage } from '@/utils/getErrorMessage';
import alert from '@/utils/shared/alert';
import PublishLessonForm from './PublishLessonForm';

type PublishLessonFormServiceProps = {
  onClose: () => void;
  coursePathSlug: string;
  cache: CacheManager<CoursePath>;
  onPublished: (coursePath: CoursePath) => void;
};

export default function PublishLessonFormService({
  onClose,
  coursePathSlug,
  cache,
  onPublished,
}: PublishLessonFormServiceProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.pedContentTab',
  });
  const { t: tWorkspace } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.courseWorkspace',
  });
  const { ref, close } = useModalDialog(true, onClose);
  const queryClient = useQueryClient();

  const { lessons, isLoading } = usePublishableLessons(coursePathSlug);

  const { mutate: publish, isPending } = useMutation(
    (lessonIds: number[]) => publishLessons(coursePathSlug, lessonIds),
    {
      onSuccess: coursePath => {
        alert.success(tWorkspace('publishSucess'));
        cache.merge(coursePath);
        queryClient.invalidateQueries({
          queryKey: lessonsQueryKeys.nestedList._def,
        });
        queryClient.invalidateQueries({
          queryKey: lessonBlocksQueryKeys._def,
        });
        onPublished(coursePath);
        close();
      },
      onError: (error: unknown) => alert.error(getErrorMessage(error)),
    },
  );

  return (
    <Modal ref={ref}>
      <Modal.Box className="flex flex-col gap-6 min-w-200">
        <div className="flex flex-col gap-2">
          <h3 className="text-lg font-bold">{t('publishLessons')}</h3>
          <p className="text-base-content/80">
            {t('publishLessonsDescription')}
          </p>
        </div>

        <ConditionalRenderer
          condition={!isLoading}
          fallback={
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-3">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
              </div>
              <div className="flex gap-3">
                <Skeleton className="h-8 w-1/2" />
                <Skeleton className="h-8 w-1/2" />
              </div>
            </div>
          }
        >
          <PublishLessonForm
            lessons={lessons}
            isLoading={isPending}
            onSubmit={({ lessonIds }) => publish(lessonIds.map(Number))}
            onCancel={close}
          />
        </ConditionalRenderer>
        <Modal.Close onClick={close} disabled={isPending} />
      </Modal.Box>
    </Modal>
  );
}
